// src/documents/xlsx/generateYuklemeCetveliXlsx.js
//
// Yükleme cetveli XLSX üreticisi — ZIP-tabanlı surgical update kullanır.
// SheetJS Community write'ı border/style kaybediyor; bu sebeple template'in
// sheet1.xml'inde yalnızca cell <v> değerleri ve <row> hidden attribute'ları
// değiştiriliyor, geri kalan her şey (styles.xml, drawings, merges) aynen
// kalır. Sonuç: OLE preview'da kenarlıklar + Excel'in "Ekle > Şekiller"
// çizgileri tam görünür.
//
// Şablon yapısı (sayfa: "Osman Tokur"):
//   A1: başlık ("YÜKLEME CETVELİ")
//   Satır 5, 8: üst tablo başlıkları (sabit)
//   Satır 12-28: 1. Panel (Z.K.T.) — özet (A12) + 9 branch (12,14,...,28)
//   Satır 30:  1. Panel TOPLAM
//   Satır 32-42: 2. Panel (N.K.T.) — özet + 6 branch (32,34,...,42)
//   Satır 44:  2. Panel TOPLAM
//   Satır 46-48: SAYAÇ PANO — özet (46) + 2. panel satırı (48)
//   Satır 50:  SAYAÇ PANO TOPLAM

import { loadXlsxTemplate } from './templateLoader';
import { applyXlsxUpdates } from './xlsxZipUpdater';

// Şablon şemaları: her template'in hücre yerleşimi farklı.
// - 'yuklemeCetveli'           → çok panel + SAYAÇ PANO (eski default)
// - 'yuklemeCetveliSingleUnit' → tek panel, sadece A1:DJ32 (Tek Bağımsız Bölüm)
// - 'yuklemeCetveliMultiMeter' → 9 katlı apartman pre-filled (Çok Sayaçlı Yapı)
//
// columnMap → her şablonun panel summary ve branch sütunları farklı; addresleri
// burada tutuyoruz. Yokluğu (null) → o tip alan o şablonda yazılmaz.
const TEMPLATE_SCHEMAS = {
  yuklemeCetveli: {
    panelBlocks: [
      { summaryRow: 12, branchRows: [12, 14, 16, 18, 20, 22, 24, 26, 28], toplamRow: 30 },
      { summaryRow: 32, branchRows: [32, 34, 36, 38, 40, 42],             toplamRow: 44 },
    ],
    columnMap: {
      summary: { name: 'A', kuruluGuc: 'H', talepGuc: 'P', kablo: 'V', akim: 'AD', talepFak: 'AJ', sigorta: 'AP' },
      branch: { linyeNo: 'AX', sigorta: 'BD', shortCircuit: 'BJ', faseR: 'BN', faseS: 'BS', faseT: 'BX', faseRST: 'CC', isik: 'CH', priz: 'CL', guc: 'CP', aciklama: 'CV' },
      toplam: { faseR: 'BN', faseS: 'BS', faseT: 'BX', faseRST: 'CC', isik: 'CH', priz: 'CL', guc: 'CP', aciklama: 'CV' },
    },
    sayacPanoRows: [46, 48],
    sayacPanoToplamRow: 50,
    dimensionRef: 'A1:DJ51',
    skipFixedCellPatches: false,
  },
  yuklemeCetveliSingleUnit: {
    panelBlocks: [
      { summaryRow: 12, branchRows: [12, 14, 16, 18, 20, 22, 24, 26, 28], toplamRow: 30 },
    ],
    columnMap: {
      summary: { name: 'A', kuruluGuc: 'H', talepGuc: 'P', kablo: 'V', akim: 'AD', talepFak: 'AJ', sigorta: 'AP' },
      branch: { linyeNo: 'AX', sigorta: 'BD', shortCircuit: 'BJ', faseR: 'BN', faseS: 'BS', faseT: 'BX', faseRST: 'CC', isik: 'CH', priz: 'CL', guc: 'CP', aciklama: 'CV' },
      toplam: { faseR: 'BN', faseS: 'BS', faseT: 'BX', faseRST: 'CC', isik: 'CH', priz: 'CL', guc: 'CP', aciklama: 'CV' },
    },
    sayacPanoRows: null,
    sayacPanoToplamRow: null,
    dimensionRef: 'A1:DJ32',
    skipFixedCellPatches: false,
  },
  yuklemeCetveliMultiMeter: {
    // 10 panel slot, her biri 2-satır branch pair'leriyle. toplamRow yok
    // (her bloğun summary'i zaten panel-level toplam görevini görüyor).
    panelBlocks: [
      { summaryRow: 12,  branchRows: [12, 14, 16, 18, 20, 22, 24, 26],                                       toplamRow: null }, // slot 1: 8 branch
      { summaryRow: 28,  branchRows: [28, 30, 32, 34, 36, 38, 40, 42, 44],                                   toplamRow: null }, // slot 2: 9 branch
      { summaryRow: 46,  branchRows: [46, 48, 50, 52, 54, 56],                                               toplamRow: null }, // slot 3: 6 branch
      { summaryRow: 58,  branchRows: [58, 60, 62, 64],                                                       toplamRow: null }, // slot 4: 4 branch (ASANSÖR)
      { summaryRow: 66,  branchRows: [66, 68, 70, 72, 74, 76, 78, 80, 82, 84, 86],                           toplamRow: null }, // slot 5: 11 branch
      { summaryRow: 88,  branchRows: [88, 90, 92, 94, 96, 98, 100, 102, 104, 106, 108],                      toplamRow: null }, // slot 6
      { summaryRow: 110, branchRows: [110, 112, 114, 116, 118, 120, 122, 124, 126, 128, 130],                toplamRow: null }, // slot 7
      { summaryRow: 132, branchRows: [132, 134, 136, 138, 140, 142, 144, 146, 148, 150, 152],                toplamRow: null }, // slot 8
      { summaryRow: 154, branchRows: [154, 156, 158, 160, 162, 164, 166, 168, 170, 172, 174, 176],           toplamRow: null }, // slot 9
      { summaryRow: 178, branchRows: [178, 180, 182, 184, 186, 188, 190, 192, 194, 196, 198],                toplamRow: null }, // slot 10
    ],
    columnMap: {
      summary: { name: 'A', kuruluGuc: 'L', talepGuc: 'T', kablo: 'Z', akim: null, talepFak: 'AH', sigorta: 'AN' },
      branch: { linyeNo: 'AV', sigorta: 'BB', shortCircuit: 'BH', isik: 'BL', priz: 'BP', faseR: 'BT', faseS: 'BY', faseT: 'CD', faseRST: 'CI', guc: 'CN', aciklama: 'CT' },
      toplam: null,
      // ÖZET / SAYAÇ PANO sütunları — template formülleri (row 210, =L178, =T178)
      // baz alınarak çıkarıldı. Header etiketleri değil, gerçek formül semantiği
      // önemlidir; T210 = Kurulu Güç (panel block L sütunu), AC210 = Talep Fak,
      // AI210 = Talep Güç, vb.
      sayac: {
        name: 'A',          // Pano/Tablo Adı (A:S merged)
        kuruluGuc: 'T',     // Kurulu Güç (W) (T:Y merged) — formül: =L{panel}
        talepFak: 'AC',     // Talep Fak. (%) (AC:AH merged) — formül: =AH{panel}
        talepGuc: 'AI',     // Talep Güç (W) (AI:AM merged) — formül: =T{panel}
        akim: 'AQ',         // Akım (A) (AQ:AT merged) — derived
        akimTasKap: 'AY',   // Akım Taş. Kap. (A) (AY:BB merged)
        kolon: 'BE',        // Kolon Hattı (BE:BT merged) — formül: =Z{panel}
        sayacKesici: 'BU',  // Kat Pano > Sayaç Kesici (BU:CA merged)
        kesici: 'CB',       // Kat Pano > Kesici (CB:CH merged) — formül: =AN{panel}
        aciklama: 'CI',     // AÇIKLAMA (CI:DH merged)
      },
    },
    // ÖZET (Müşterek Pano) — sayaç slot satırları + TOPLAM satırı.
    // Slot rows 208-234 (14 panel), TOPLAM row 236. Her satır 2-satır pair.
    // Row 208: template'in pre-filled ASANSÖR satırı (formül =A58/=L58 ile panel
    // block 4'ten çekiyor). Bu satırı sayacPanoRows'a ekleyince generator
    // user'ın gerçek panel'ini buraya yazıp ASANSÖR'ü override ediyor; eğer
    // user'ın paneli yoksa satır gizleniyor → orphan ASANSÖR kaybolur.
    sayacPanoRows: [208, 210, 212, 214, 216, 218, 220, 222, 224, 226, 228, 230, 232, 234],
    sayacPanoToplamRow: 236,
    // TOPLAM GÜÇ HESABI bölümü (rows 243-258). Template'de label'lar
    // scrambled (Yangın İhbar / 50-100 / Notlar). Generator override eder.
    // Her kategori 3-satır pair (örn. 246-248, 249-251).
    toplamGucHesabi: {
      columnMap: { name: 'BH', kuruluGuc: 'BS', talepFak: 'CH', talepGuc: 'CT' },
      categories: [
        { key: 'daire',    label: 'DAİRE',         row: 246 },
        { key: 'asansor',  label: 'ASANSÖR',       row: 249 },
        { key: 'isyeri',   label: 'İŞ YERİ',       row: 252 },
        { key: 'musterek', label: 'MÜŞTEREK',      row: 255 },
      ],
      genelToplamRow: 258,
    },
    // Template'te eksik header merge'leri — Kurulu Güç (T:Y) ve Talep Güç
    // (AI:AM) sütun başlıkları tek hücrede sıkışık görünüyordu. Generator
    // bunları runtime'da merge ediyor.
    extraMergeCells: ['T204:Y207', 'AI204:AM207'],
    // Eksik kenarlıklar — runtime'da styles.xml üzerinden enjekte ediliyor.
    // Merge yapılarına göre hedef hücre adresleri hesaplandı:
    //
    //   ÖZET TOPLAM satırı (sayacPanoToplamRow=236) row 236-237 merged
    //   → bottom border row 237'ye uygulanır (merge'in alttaki satırı).
    //
    //   TOPLAM GÜÇ HESABI text 3 stacked merge: CI228:DH229, CI230:DH231,
    //   CI232:DH233. Bottom edge = row 233, kolonlar CI:DH (text genişliği).
    //
    //   "Notlar:" hücresi tek merge: BH255:BR257.
    //   → right edge = BR255:BR257 (en sağ sütun)
    //   → bottom edge = BH257:BR257 (en alt satır)
    cellBorders: [
      { cells: ['A237:DH237'], borders: { bottom: 'thick' } },
      { cells: ['CI233:DH233'], borders: { bottom: 'thick' } },
      { cells: ['BR255:BR257'], borders: { right: 'thick' } },
      { cells: ['BH257:BR257'], borders: { bottom: 'thick' } },
    ],
    // Şablonda 443 satır tanımlı (kullanıcı +2 satır ekledi); gerçek içerik
    // ~row 259'da bitiyor; sonrası boş.
    dimensionRef: 'A1:DI259',
    hiddenRowsExtra: { from: 260, to: 443 },
    skipFixedCellPatches: true,
    // Slot label'ları — sequential mapping yerine isim-bazlı eşleme için.
    slotLabels: ['', 'Z.K.T.-1', 'Z.K.T.-2', 'ASANSÖR', 'Z.K.T.-3', 'N.K.T.-1', 'N.K.T.-2', 'N.K.T.-3', '', ''],
  },
};

function num(v) {
  if (typeof v === 'number') return v;
  if (typeof v !== 'string') return v;
  const f = parseFloat(v);
  return Number.isNaN(f) ? v : f;
}

/** Branch'in fazına göre güç sütunu (R/S/T/RST = BN/BS/BX/CC). */
function phaseColumn(phase) {
  switch ((phase || '').toUpperCase()) {
    case 'R':   return 'BN';
    case 'S':   return 'BS';
    case 'T':   return 'BX';
    case 'RST': return 'CC';
    default:    return 'CC';
  }
}

/** MCB objesinden şablon formatında title üret: "{curve}-{pole}x{current}A".
 *  MCB.title genellikle "TipB,4P,25A,10kA" formatında olduğu için yeniden
 *  formatlamak gerekiyor. mcb yoksa fallback değeri döner.
 */
function formatBreakerTitle(mcb, fallback = '') {
  if (!mcb) return fallback;
  const curve = mcb.curve || '';
  const poleNum = String(mcb.pole || '').replace(/\D+/g, '') || mcb.polar || '';
  const current = mcb.current ?? '';
  if (!curve || !current) return mcb.title || fallback;
  return `${curve}-${poleNum}x${current}A`;
}

/** Branch listesinden R/S/T/RST + light/plug toplamları üret. */
function computePanelTotals(branchCircuits) {
  const t = { r: 0, s: 0, t: 0, rst: 0, lightCount: 0, plugCount: 0 };
  for (const br of branchCircuits || []) {
    const phase = (br.phase || '').toUpperCase();
    const p = br.phasePower ?? br.totalPower ?? 0;
    if (phase === 'R')        t.r   += p;
    else if (phase === 'S')   t.s   += p;
    else if (phase === 'T')   t.t   += p;
    else if (phase === 'RST') t.rst += p;
    t.lightCount += br.lightCount || 0;
    t.plugCount  += br.plugCount  || 0;
  }
  return t;
}

function sumAllTotals(panels, panelTotals) {
  const t = { r: 0, s: 0, t: 0, rst: 0, lightCount: 0, plugCount: 0 };
  for (const p of panels) {
    const pt = panelTotals[p.id] || {};
    t.r          += pt.r          || 0;
    t.s          += pt.s          || 0;
    t.t          += pt.t          || 0;
    t.rst        += pt.rst        || 0;
    t.lightCount += pt.lightCount || 0;
    t.plugCount  += pt.plugCount  || 0;
  }
  return t;
}

/**
 * Yükleme cetveli XLSX dosyasını oluşturur.
 * Beklenen `data` yapısı için adaptYCDataToXlsx çıktısına bak.
 *
 * @param {Object} data
 * @param {Object} [opts]
 * @param {string} [opts.templateName='yuklemeCetveli'] - public/templates/{name}.xlsx
 *   Tek Bağımsız Bölüm projeleri için 'yuklemeCetveliSingleUnit' geçilebilir.
 * @returns {Promise<ArrayBuffer>}
 */
export async function generateYuklemeCetveliXlsx(data, opts = {}) {
  const { templateName = 'yuklemeCetveli' } = opts;
  const schema = TEMPLATE_SCHEMAS[templateName] || TEMPLATE_SCHEMAS.yuklemeCetveli;
  const PANEL_BLOCKS = schema.panelBlocks;
  const SAYAC_PANO_PANEL_ROWS = schema.sayacPanoRows;
  const SAYAC_PANO_TOPLAM_ROW = schema.sayacPanoToplamRow;
  const buf = await loadXlsxTemplate(templateName);

  const panels = (data?.panels || []).slice(0, PANEL_BLOCKS.length);
  if ((data?.panels?.length || 0) > PANEL_BLOCKS.length) {
    console.warn(`[generateYuklemeCetveliXlsx] Şablon en fazla ${PANEL_BLOCKS.length} panel destekler — ${data.panels.length - PANEL_BLOCKS.length} panel kırpıldı.`);
  }

  const cells = {};
  const hiddenRows = [];
  const panelTotals = {};

  // SingleUnit özel düzenleme: row 32 boş kapanış frame'i (içeriksiz,
  // kenarlıksız) → tablonun altında gereksiz boşluk yaratıyor. Gizleyince
  // tablo row 31'de biter ve row 31'in thickBot stiliyle doğal alt kenarlık
  // çıkar. Branch satırları (12-13, 14-15, …) merged 2-satır pair olarak
  // tasarlandığı için 2. satırlarına dokunma — yoksa visual yüksekliği yarıya
  // iner.
  if (templateName === 'yuklemeCetveliSingleUnit') {
    hiddenRows.push(32);
  }

  // MultiMeter / diğer şablonlar: schema.hiddenRowsExtra ile aralık tanımı
  if (schema.hiddenRowsExtra && Number.isFinite(schema.hiddenRowsExtra.from)) {
    const { from, to } = schema.hiddenRowsExtra;
    for (let r = from; r <= to; r++) hiddenRows.push(r);
  }

  const COL = schema.columnMap || {};
  const SUM = COL.summary || {};
  const BR  = COL.branch  || {};
  const TPL = COL.toplam;

  // Branch fazına göre güç sütunu — columnMap'ten oku
  function phaseColForBranch(phase) {
    const p = (phase || '').toUpperCase();
    if (p === 'R') return BR.faseR;
    if (p === 'S') return BR.faseS;
    if (p === 'T') return BR.faseT;
    if (p === 'RST') return BR.faseRST;
    return BR.faseRST || BR.faseR;
  }

  // Hücre yazımı için yardımcı (sütun null ise atla)
  const setCell = (col, row, value) => { if (col) cells[col + row] = value; };

  // ── Her panel: özet + branch satırları + (varsa) TOPLAM ──
  panels.forEach((panel, panelIdx) => {
    const block = PANEL_BLOCKS[panelIdx];
    if (!block) return;

    // Panel özet (block içinde merged tek hücre — top-left'e yaz)
    setCell(SUM.name,      block.summaryRow, panel.name || '');
    setCell(SUM.kuruluGuc, block.summaryRow, panel.installedPower ?? 0);
    setCell(SUM.talepGuc,  block.summaryRow, panel.demandPower    ?? 0);
    setCell(SUM.kablo,     block.summaryRow, panel.cableTitle     || '');
    setCell(SUM.akim,      block.summaryRow, num(panel.current));
    setCell(SUM.talepFak,  block.summaryRow, panel.demandFactor   || '');
    setCell(SUM.sigorta,   block.summaryRow, panel.mainBreakerTitle || '');

    const branches = panel.branchCircuits || [];
    if (branches.length > block.branchRows.length) {
      console.warn(`[YC] Panel "${panel.name}" — ${branches.length} branch var, slot ${block.branchRows.length} destekler — fazlası kırpıldı.`);
    }
    branches.slice(0, block.branchRows.length).forEach((br, i) => {
      const row = block.branchRows[i];
      setCell(BR.linyeNo, row, i + 1);
      setCell(BR.sigorta, row, br.breakerTitle || '');
      setCell(BR.shortCircuit, row, br.shortCircuitTitle || '');
      const phaseCol = phaseColForBranch(br.phase);
      const power = br.phasePower ?? br.totalPower ?? 0;
      // Tüm faz sütunlarını yazıyoruz: aktif faza güç, diğerlerine 0.
      // Önemli: template'te formül cache'i var (örn. BY12 =CN12, cached=200);
      // yalnız aktif faza yazsak diğer hücrelerin cached değeri kalır ve
      // çıkışı olmadığı halde 200W gibi değerler görünür. Açıkça 0 yazınca
      // formül + cache override edilir.
      [BR.faseR, BR.faseS, BR.faseT, BR.faseRST].forEach(col => {
        if (!col) return;
        cells[col + row] = (col === phaseCol) ? power : 0;
      });
      if (br.lightCount != null && br.lightCount !== 0) setCell(BR.isik, row, br.lightCount);
      if (br.plugCount  != null && br.plugCount  !== 0) setCell(BR.priz, row, br.plugCount);
      setCell(BR.guc, row, br.totalPower ?? br.phasePower ?? 0);
      setCell(BR.aciklama, row, br.description || '');
    });

    // Kullanılmayan branch slot'larını gizle (sadece toplamRow varsa — çünkü
    // toplam yoksa block.branchRows.length zaten kullanılan kadar tutulabilir;
    // ama MultiMeter'da tüm slot'lar pre-styled olduğundan boş kalan branch
    // satırlarını gizlemek tablonun altında "boş satır" izlenimini önler).
    const usedBranchCount = Math.min(branches.length, block.branchRows.length);
    if (usedBranchCount < block.branchRows.length) {
      const firstUnusedIdx = usedBranchCount;
      const lastBranchPair = block.branchRows[block.branchRows.length - 1] + 1;
      const cap = Number.isFinite(block.toplamRow) ? block.toplamRow : (lastBranchPair + 1);
      const firstUnusedRow = block.branchRows[firstUnusedIdx];
      for (let r = firstUnusedRow; r < cap; r++) hiddenRows.push(r);
    }

    // Panel TOPLAM (varsa)
    const totals = computePanelTotals(branches);
    panelTotals[panel.id] = totals;
    if (TPL && Number.isFinite(block.toplamRow)) {
      setCell(TPL.faseR, block.toplamRow, totals.r ?? 0);
      setCell(TPL.faseS, block.toplamRow, totals.s ?? 0);
      setCell(TPL.faseT, block.toplamRow, totals.t ?? 0);
      if (totals.rst) setCell(TPL.faseRST, block.toplamRow, totals.rst);
      setCell(TPL.isik, block.toplamRow, totals.lightCount ?? 0);
      setCell(TPL.priz, block.toplamRow, totals.plugCount  ?? 0);
      setCell(TPL.guc,  block.toplamRow,
        (totals.r ?? 0) + (totals.s ?? 0) + (totals.t ?? 0) + (totals.rst ?? 0));
      setCell(TPL.aciklama, block.toplamRow, 'TOPLAM');
    }
  });

  // Tamamen kullanılmayan panel bloklarını gizle
  for (let i = panels.length; i < PANEL_BLOCKS.length; i++) {
    const block = PANEL_BLOCKS[i];
    // Block sınırı: toplamRow varsa onu, yoksa son branch pair'in alt satırı
    const lastBranchPair = block.branchRows[block.branchRows.length - 1] + 1;
    const blockEnd = Number.isFinite(block.toplamRow) ? (block.toplamRow + 1) : lastBranchPair;
    for (let r = block.summaryRow; r <= blockEnd; r++) hiddenRows.push(r);
  }

  // ── SAYAÇ PANO / ÖZET bloğu (sadece şemada tanımlıysa) ──
  if (panels.length > 0 && SAYAC_PANO_PANEL_ROWS) {
    const SAYAC = COL.sayac;

    if (SAYAC) {
      // MultiMeter şablonu — kolonlar name/kuruluGuc/talepFak/talepGuc/akim/...
      // Sadece ana panelden (meter_panel) beslenen GERÇEK panelleri al; eski
      // test komponentleri (örn. saved JBZ'deki orphan G.T./ASANSÖR) feeder
      // bağlantısı olmadığı için filtrelenir.
      const realSayacPanels = panels;
      // (Filtreleme transformSimpleSupplyToYC_Data sonrasında zaten panels[]
      // dist_panel/panel olanları içerir; ek filtre gerekirse burada uygulanır.
      // Şu an feeder linkini kontrol etmiyoruz çünkü panels objesi feeder
      // bilgisi içermiyor; filter kararı YC transform'da yapılmalı.)

      const visiblePanels = realSayacPanels.slice(0, SAYAC_PANO_PANEL_ROWS.length);
      visiblePanels.forEach((panel, i) => {
        const row = SAYAC_PANO_PANEL_ROWS[i];
        const breakerTitle = formatBreakerTitle(panel.panelMainBreaker, panel.mainBreakerTitle || '');
        setCell(SAYAC.name,        row, panel.name || '');
        setCell(SAYAC.kuruluGuc,   row, panel.installedPower ?? 0);
        setCell(SAYAC.talepFak,    row, panel.demandFactor || '');
        setCell(SAYAC.talepGuc,    row, panel.demandPower ?? 0);
        setCell(SAYAC.akim,        row, num(panel.current));
        setCell(SAYAC.akimTasKap,  row, panel.cableCurrentLimit ?? '');
        setCell(SAYAC.kolon,       row, panel.cableTitle || '');
        // Sayaç Kesicisi ve Kat Pano Kesicisi: supply'da ikisi için ayrı veri
        // yok, ikisi de panelMainBreaker'dan türetiliyor. Template'in static
        // pre-fill ve formula cache'lerini override etmek için her ikisini de
        // açıkça yazıyoruz.
        setCell(SAYAC.sayacKesici, row, breakerTitle);
        setCell(SAYAC.kesici,      row, breakerTitle);
      });
      // Kullanılmayan sayaç satırlarını (ve pair'lerini) gizle
      for (let i = visiblePanels.length; i < SAYAC_PANO_PANEL_ROWS.length; i++) {
        const row = SAYAC_PANO_PANEL_ROWS[i];
        hiddenRows.push(row, row + 1);
      }

      // TOPLAM satırı (varsa) — son satır, üst sayaç satırlarının toplamı.
      // MainPanel'in kolon hattı / kapasitesi / kesicisi de bu satırda gösterilir
      // (proje-level değerler — sayaç tablosunun "ana çıkış" özeti).
      if (SAYAC_PANO_TOPLAM_ROW != null) {
        const sumInstalled = visiblePanels.reduce((s, p) => s + (p.installedPower || 0), 0);
        const sumDemand    = visiblePanels.reduce((s, p) => s + (p.demandPower    || 0), 0);
        const sumCurrent   = visiblePanels.reduce((s, p) => s + (parseFloat(p.current) || 0), 0);
        const adp = data?.adp || {};
        const adpBreakerTitle = formatBreakerTitle(adp.breaker, '');

        setCell(SAYAC.name,        SAYAC_PANO_TOPLAM_ROW, 'TOPLAM');
        setCell(SAYAC.kuruluGuc,   SAYAC_PANO_TOPLAM_ROW, sumInstalled);
        setCell(SAYAC.talepFak,    SAYAC_PANO_TOPLAM_ROW, '');
        setCell(SAYAC.talepGuc,    SAYAC_PANO_TOPLAM_ROW, sumDemand);
        setCell(SAYAC.akim,        SAYAC_PANO_TOPLAM_ROW, sumCurrent.toFixed(2));
        // ADP cable: earthCurCap (kullanıcı kuralı: MainPanel için toprak kapasitesi)
        setCell(SAYAC.akimTasKap,  SAYAC_PANO_TOPLAM_ROW, adp.cableCurrentLimit ?? '');
        setCell(SAYAC.kolon,       SAYAC_PANO_TOPLAM_ROW, adp.cableTitle || '');
        // MainPanel breaker'ı her iki kesici hücresine yaz (template'in
        // "4x125A" static cache'ini override et)
        setCell(SAYAC.sayacKesici, SAYAC_PANO_TOPLAM_ROW, adpBreakerTitle);
        setCell(SAYAC.kesici,      SAYAC_PANO_TOPLAM_ROW, adpBreakerTitle);
      }

      // ─── TOPLAM GÜÇ HESABI bölümü ───
      const tgh = schema.toplamGucHesabi;
      if (tgh) {
        const TGH = tgh.columnMap;
        const adp = data?.adp || {};
        const powers = adp.powers || {};

        // Kategori sınıflandırma: panel.subscribeType → key
        const categoryOf = (panel) => {
          const t = (panel.subscribeType || '').toLowerCase();
          if (t === 'asansör' || t === 'asansor') return 'asansor';
          if (t === 'müşterek' || t === 'musterek') return 'musterek';
          if (t === 'işyeri' || t === 'isyeri' || t === 'ticari') return 'isyeri';
          if (t === 'mesken') return 'daire';
          return null;
        };

        // Her kategori için sums (ADP powers'tan al; yoksa panellerden hesapla)
        const catData = {
          daire:    { kuruluGuc: powers.daireTotalPower    || 0, talepFak: powers.daireDemandFakt    || '', talepGuc: powers.daireDemandPower    || 0 },
          asansor:  { kuruluGuc: powers.asansorTotalPower  || 0, talepFak: powers.asansorDemandFakt  || '', talepGuc: powers.asansorDemandPower  || 0 },
          isyeri:   { kuruluGuc: powers.isyeriTotalPower   || 0, talepFak: powers.isyeriDemandFakt   || '', talepGuc: powers.isyeriDemandPower   || 0 },
          musterek: { kuruluGuc: powers.musterekTotalPower || 0, talepFak: powers.musterekDemandFakt || '', talepGuc: powers.musterekDemandPower || 0 },
        };

        // Kategori başına panel var mı kontrolü (subscribeType ile)
        const categoryPresent = { daire: false, asansor: false, isyeri: false, musterek: false };
        visiblePanels.forEach(p => {
          const c = categoryOf(p);
          if (c) categoryPresent[c] = true;
        });

        // Fallback: ADP powers yoksa panelden hesapla
        Object.keys(catData).forEach(key => {
          if (catData[key].kuruluGuc === 0 && catData[key].talepGuc === 0 && categoryPresent[key]) {
            const matching = visiblePanels.filter(p => categoryOf(p) === key);
            catData[key].kuruluGuc = matching.reduce((s, p) => s + (p.installedPower || 0), 0);
            catData[key].talepGuc  = matching.reduce((s, p) => s + (p.demandPower    || 0), 0);
            catData[key].talepFak  = matching[0]?.demandFactor || '';
          }
        });

        // Her kategori satırı: panel varsa yaz, yoksa 3-satırlık merge gizle
        let genelKurulu = 0, genelTalep = 0;
        tgh.categories.forEach(cat => {
          if (categoryPresent[cat.key]) {
            const d = catData[cat.key];
            setCell(TGH.name,      cat.row, cat.label);
            setCell(TGH.kuruluGuc, cat.row, d.kuruluGuc);
            setCell(TGH.talepFak,  cat.row, d.talepFak);
            setCell(TGH.talepGuc,  cat.row, d.talepGuc);
            genelKurulu += d.kuruluGuc;
            genelTalep  += d.talepGuc;
          } else {
            // Kategori panel yok → 3-satır merge'i gizle (row, row+1, row+2)
            for (let r = cat.row; r <= cat.row + 2; r++) hiddenRows.push(r);
          }
        });

        // GENEL TOPLAM satırı (varsa)
        if (Number.isFinite(tgh.genelToplamRow)) {
          setCell(TGH.name,      tgh.genelToplamRow, 'GENEL TOPLAM');
          setCell(TGH.kuruluGuc, tgh.genelToplamRow, genelKurulu);
          setCell(TGH.talepFak,  tgh.genelToplamRow, '');
          setCell(TGH.talepGuc,  tgh.genelToplamRow, genelTalep);
        }
      }
    } else if (SAYAC_PANO_TOPLAM_ROW != null) {
      // Klasik yuklemeCetveli şablonu — eski sütun adresleri.
      const totalInstalled = panels.reduce((s, p) => s + (p.installedPower || 0), 0);
      const totalDemand    = panels.reduce((s, p) => s + (p.demandPower    || 0), 0);
      const firstPanel = panels[0];

      for (let i = 0; i < SAYAC_PANO_PANEL_ROWS.length; i++) {
        const row = SAYAC_PANO_PANEL_ROWS[i];
        if (i >= panels.length) {
          hiddenRows.push(row, row + 1);
          continue;
        }
        const panel  = panels[i];
        const totals = panelTotals[panel.id] || {};

        if (i === 0) {
          cells['A'  + row] = 'SAYAÇ PANO';
          cells['H'  + row] = totalInstalled;
          cells['P'  + row] = totalDemand;
          cells['V'  + row] = firstPanel.feederCableTitle || '';
          cells['AD' + row] = num(firstPanel.feederCurrent);
          cells['AJ' + row] = firstPanel.demandFactor || '';
          cells['AP' + row] = firstPanel.feederBreakerTitle || '';
        }

        cells['AX' + row] = i + 1;
        cells['BD' + row] = panel.feederBreakerTitle || '';
        cells['BJ' + row] = panel.feederShortCircuitTitle || '';
        cells['BN' + row] = totals.r ?? 0;
        cells['BS' + row] = totals.s ?? 0;
        cells['BX' + row] = totals.t ?? 0;
        if (totals.rst) cells['CC' + row] = totals.rst;
        cells['CH' + row] = totals.lightCount ?? 0;
        cells['CL' + row] = totals.plugCount  ?? 0;
        cells['CP' + row] = panel.installedPower ?? 0;
        cells['CV' + row] = panel.name || '';
      }

      // SAYAÇ PANO TOPLAM
      const total = sumAllTotals(panels, panelTotals);
      cells['BN' + SAYAC_PANO_TOPLAM_ROW] = total.r;
      cells['BS' + SAYAC_PANO_TOPLAM_ROW] = total.s;
      cells['BX' + SAYAC_PANO_TOPLAM_ROW] = total.t;
      if (total.rst) cells['CC' + SAYAC_PANO_TOPLAM_ROW] = total.rst;
      cells['CH' + SAYAC_PANO_TOPLAM_ROW] = total.lightCount;
      cells['CL' + SAYAC_PANO_TOPLAM_ROW] = total.plugCount;
      cells['CP' + SAYAC_PANO_TOPLAM_ROW] =
        (total.r || 0) + (total.s || 0) + (total.t || 0) + (total.rst || 0);
      cells['CV' + SAYAC_PANO_TOPLAM_ROW] = 'TOPLAM';
    }
  } else if (SAYAC_PANO_PANEL_ROWS) {
    // Panel yoksa tüm sayaç satırlarını gizle
    SAYAC_PANO_PANEL_ROWS.forEach(row => { hiddenRows.push(row, row + 1); });
  }

  // Şablon-spesifik sabit cell patch'leri (MultiMeter pre-filled olduğu için skip)
  if (!schema.skipFixedCellPatches) {
    // Başlık (data verirse override)
    if (data?.title) cells['A1'] = data.title;
    // Sabit düzeltmeler
    cells['BD8'] = 'Io';
  }

  return await applyXlsxUpdates(buf, {
    cells,
    hiddenRows,
    dimensionRef: schema.dimensionRef,
    mergeCells: schema.extraMergeCells,
    cellBorders: schema.cellBorders,
  });
}

export default generateYuklemeCetveliXlsx;
